import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, BriefcaseIcon, ArrowRightIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import EnTitle from '../components/EnTitle';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';

export default function Careers() {
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState({});
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    API.get('/careers').then(r => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const introTitle = (language === 'ja' && settings.careers_intro_title_ja) || settings.careers_intro_title || t('careers.defaultTitle');
  const introContent = (language === 'ja' && settings.careers_intro_content_ja) || settings.careers_intro_content || t('careers.defaultIntro');
  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <PageLayout>
      <SEO title={t('careers.title')} description="Explore open job opportunities at Sankyo Machine Tools Philippines Inc." />
      <PageBanner en="CAREERS" title={t('careers.title')} subtitle={t('careers.tagline')} breadcrumbs={[{ label: t('careers.title') }]} />

      {/* Intro — text and image side-by-side */}
      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-ink mb-6 leading-snug border-l-4 border-primary pl-4">{introTitle}</h2>
            {introContent.split('\n\n').map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed mb-3">{p}</p>
            ))}
          </div>
          <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
            {settings.careers_image_url ? (
              <img src={imgSrc(settings.careers_image_url)} alt={introTitle} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-gray-100">
                <UserGroupIcon className="w-16 h-16 text-primary/20" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Job openings — card grid */}
      <section id="open-positions" className="py-20 lg:py-28 bg-[#f5f5f5]">
        <div className="w-[90%] max-w-6xl mx-auto">
          <div className="mb-12">
            <EnTitle lines={['OPEN', 'POSITIONS']} sub={t('careers.openPositions')} className="text-5xl sm:text-6xl lg:text-7xl" />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-56 bg-white animate-pulse" />)}
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">{t('careers.noOpenings')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobs.map((job, i) => (
                <Link key={job.id} to={`/careers/${job.slug}`}
                  className="card fade-up group flex flex-col overflow-hidden"
                  style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className="h-28 bg-charcoal flex items-center justify-center">
                    <UserGroupIcon className="w-10 h-10 text-white/70" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-bold text-ink text-lg group-hover:text-primary transition-colors mb-2">{job.title}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-xs text-gray-500">
                      {job.location && (
                        <span className="flex items-center gap-1"><MapPinIcon className="w-3.5 h-3.5" /> {job.location}</span>
                      )}
                      {job.employment_type && (
                        <span className="flex items-center gap-1"><BriefcaseIcon className="w-3.5 h-3.5" /> {job.employment_type}</span>
                      )}
                    </div>
                    <span className="mt-auto flex items-center gap-1.5 font-btn text-xs font-semibold tracking-widest uppercase text-primary group-hover:text-ink transition-colors">
                      {t('careers.explore')} <ArrowRightIcon className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
